import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Form, Input, Alert, Typography, Button, Tooltip, Upload, Popconfirm, Spin } from 'antd';
import {
	InfoCircleOutlined,
	DeleteOutlined,
	UploadOutlined,
	DeleteFilled,
} from '@ant-design/icons';
import { BulkCallTransferData } from '@/interfaces/ITweakAdvancedSettings';
import { DYNAMIC_CALL_TRANSFER_ENTRY_LIMIT } from 'src/constants/agentSettings';
import * as XLSX from 'xlsx';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const LazyTable = lazy(() => import('antd').then((module) => ({ default: module.Table })));

interface BulkTransfersFormSectionProps {
	value?: BulkCallTransferData[];
	onChange?: (value: BulkCallTransferData[]) => void;
}

const BulkTransfersFormSection: React.FC<BulkTransfersFormSectionProps> = ({
	value = [],
	onChange,
}) => {
	const [rawInput, setRawInput] = useState('');
	// const [inputMethod, setInputMethod] = useState('file');
	const inputMethod = 'file';
	const [errors, setErrors] = useState<string[]>([]);
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);
	const [showTable, setShowTable] = useState(false);
	const normalizePhoneNumber = (str: string): string => str.replace(/[\s()-]/g, '');

	const parseBulkTransfers = (
		input: string,
		existing: BulkCallTransferData[] = [],
	): { parsed: BulkCallTransferData[]; errors: string[] } => {
		const normalizedInput = input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
		const lines = normalizedInput.split('\n').filter((line) => line.trim().length > 0);
		const parsed: BulkCallTransferData[] = [];
		const errors: string[] = [];

		const phoneRegex = /\+[\d\s()\-]{7,}/g;
		const existingNumbers = new Set(existing.map((e) => e.number));

		lines.forEach((rawLine, idx) => {
			const line = rawLine.trim();
			const matches = [...line.matchAll(phoneRegex)];

			if (matches.length === 0) {
				errors.push(`Line ${idx + 1}: No valid phone number found.`);
				return;
			}

			if (matches.length > 1) {
				errors.push(`Line ${idx + 1}: Multiple phone numbers found.`);
				return;
			}

			const rawNumber = matches[0][0];
			let number = normalizePhoneNumber(rawNumber);
			number = number.startsWith('+') ? number : `+${number}`;

			let validPhone = '';

			const phoneObj = parsePhoneNumberFromString(number);
			if (phoneObj && phoneObj.isValid()) {
				validPhone = phoneObj.format('E.164');
			}

			if (!validPhone) {
				errors.push(`Line ${idx + 1}: Invalid phone number format.`);
				return;
			}

			if (existingNumbers.has(validPhone)) {
				errors.push(`Line ${idx + 1}: Number already exists.`);
				return;
			}

			const scenario = line.replace(rawNumber, '').trim() || `Scenario ${idx + 1}`;
			parsed.push({ scenario, number: validPhone });
			existingNumbers.add(validPhone);
		});

		return { parsed, errors };
	};

	const handleDownloadTemplate = () => {
		const ws = XLSX.utils.json_to_sheet([{ scenario: '', number: '' }]);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Scenarios');
		XLSX.writeFile(wb, 'bulk_transfer_template.xlsx');
	};
	const handleFileUpload = (file: File) => {
		if (!file) {
			return false;
		}

		const allowedExtensions = ['.xlsx', '.xls', '.csv'];
		const fileName = file.name.toLowerCase();
		const hasValidExtension = allowedExtensions.some((ext) => fileName.endsWith(ext));

		if (!hasValidExtension) {
			setErrors([
				`Invalid file type. Please upload a file with one of these extensions: ${allowedExtensions.join(', ')}`,
			]);
			return false;
		}

		const reader = new FileReader();
		setUploadedFile(file);

		reader.onload = (event) => {
			try {
				const data = event.target?.result;
				const workbook = XLSX.read(data, { type: 'array' });
				const sheet = workbook.Sheets[workbook.SheetNames[0]];
				const json = XLSX.utils.sheet_to_json<Record<string, string>>(sheet);

				const parsed: BulkCallTransferData[] = [];
				const uploadErrors: string[] = [];

				const existingNumbers = new Set(value.map((entry) => entry.number));

				json.forEach((row, idx) => {
					const rawNumber = row.number?.toString().trim();
					const number = normalizePhoneNumber(rawNumber || '');

					const scenario = row.scenario?.toString().trim() || '';

					let validPhone = '';

					const phoneObj = number.startsWith('+')
						? parsePhoneNumberFromString(number)
						: parsePhoneNumberFromString(number, 'US');

					if (phoneObj && phoneObj.isValid()) {
						validPhone = phoneObj.format('E.164');
					}

					if (!rawNumber || !scenario) {
						uploadErrors.push(`Row ${idx + 2}: Missing number or scenario`);
					} else if (!validPhone) {
						uploadErrors.push(`Row ${idx + 2}: Invalid phone number format`);
					} else if (existingNumbers.has(validPhone)) {
						uploadErrors.push(`Row ${idx + 2}: Number already exists`);
					} else {
						parsed.push({ scenario, number: validPhone });
						existingNumbers.add(validPhone);
					}
				});

				const allowedToAdd = DYNAMIC_CALL_TRANSFER_ENTRY_LIMIT - value.length;
				const finalParsed = parsed.slice(0, allowedToAdd);

				if (finalParsed.length > 0) {
					onChange?.([...value, ...finalParsed]);
				}

				if (uploadErrors.length > 0 || finalParsed.length === 0) {
					const successNote =
						finalParsed.length > 0
							? `Successfully uploaded ${finalParsed.length} new entries.`
							: '';
					setErrors([...uploadErrors, ...(successNote ? [successNote] : [])]);
				} else {
					setErrors([`Successfully uploaded ${finalParsed.length} entries from file.`]);
				}
			} catch {
				setErrors(['Error processing file. Please check file format']);
			}
		};

		reader.readAsArrayBuffer(file);
		return false;
	};

	const handleClearFile = () => {
		setUploadedFile(null);
		setErrors([]);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const val = e.target.value;
		setRawInput(val);
		const { errors } = parseBulkTransfers(val);
		setErrors(errors);
	};

	const handleParseButton = () => {
		const { parsed, errors } = parseBulkTransfers(rawInput);
		setErrors(errors);

		if (errors.length === 0 && parsed.length > 0) {
			const totalEntries = value.length + parsed.length;

			if (totalEntries > DYNAMIC_CALL_TRANSFER_ENTRY_LIMIT) {
				setErrors([
					`Cannot add ${parsed.length} entries. Total entries would exceed the limit of ${DYNAMIC_CALL_TRANSFER_ENTRY_LIMIT}. Current: ${value.length}, Adding: ${parsed.length}, Total would be: ${totalEntries}`,
				]);
				return;
			}

			onChange?.([...value, ...parsed]);
			setRawInput('');
		}
	};

	const handleDelete = (indexToDelete: number) => {
		const updated = value.filter((_, i) => i !== indexToDelete);
		onChange?.(updated);
	};

	const handleDeleteAll = () => {
		onChange?.([]);
	};

	const columns = [
		{ title: 'Scenario', dataIndex: 'scenario', key: 'scenario' },
		{ title: 'Number', dataIndex: 'number', key: 'number' },
		{
			title: 'Action',
			key: 'action',
			render: (_: any, __: any, index: number) => (
				<Button danger icon={<DeleteOutlined />} size="small" onClick={() => handleDelete(index)} />
			),
		},
	];

	useEffect(() => {
		if (value.length > 0 && !showTable) {
			setShowTable(true);
		}
	}, [value.length, showTable]);

	return (
		<div className="p-6">
			<div className="mb-6">
				<div className="flex items-start gap-3 mb-4">
					<InfoCircleOutlined className="text-blue-500 mt-1" />
					<div>
						<Typography.Paragraph className="mb-3">
							Upload bulk transfer instructions as per sample file.
						</Typography.Paragraph>
						{/* <Typography.Text type="secondary" className="block mb-3">
							Examples (for text input):
							<ul className="space-y-2 mb-4">
								<li className="text-gray-700">
									<em>If the user wants to call John, transfer to +123332324.</em>
								</li>
								<li className="text-gray-700">
									<em>If they want a refund, do +32343322.</em>
								</li>
								<li className="text-gray-700">
									<em>+1234567890 if user needs support.</em>
								</li>
							</ul>
						</Typography.Text> */}
					</div>
				</div>

				<div className="bg-gray-50 p-4 rounded-lg mb-4">
					<div className="space-y-2 text-sm bg-orange-100 outline outline-red-300 p-4 rounded-lg">
						<div className="flex items-center gap-2">
							<span className="w-2 h-2 bg-blue-500 rounded-full" />
							<Typography.Text strong>Each instruction must be on a new Row.</Typography.Text>
						</div>
						<div className="flex items-center gap-2">
							<span className="w-2 h-2 bg-blue-500 rounded-full" />
							<Typography.Text strong>
								Each non-US phone number must include a country code.
							</Typography.Text>
						</div>
						<div className="flex items-center gap-2">
							<span className="w-2 h-2 bg-blue-500 rounded-full" />
							<Typography.Text strong>Use E.164 format (e.g., +1234567890).</Typography.Text>
						</div>
					</div>
				</div>
			</div>

			{/* <div className="mb-6">
				<div className="flex justify-center">
					<Radio.Group value={inputMethod} onChange={(e) => setInputMethod(e.target.value)}>
						<Radio.Button value="text">Add using text</Radio.Button>
						<Radio.Button value="file">Add using file</Radio.Button>
					</Radio.Group>
				</div>
			</div> */}

			{inputMethod !== 'file' && (
				<div className="mb-6">
					<Form.Item
						validateStatus={errors.length ? 'warning' : ''}
						help={errors.length ? 'Fix issues before adding.' : ''}
					>
						<Input.TextArea
							rows={5}
							value={rawInput}
							onChange={handleInputChange}
							placeholder={
								'If the user wants to call John, transfer to +123332324234.\n+325245245245 if user wants to talk to sales\nTo Connect to henry, the assisstant of Carla Wood +320123456789'
							}
							style={{
								whiteSpace: rawInput ? 'nowrap' : 'pre-wrap',
								overflowX: 'auto',
								wordWrap: rawInput ? 'normal' : 'break-word',
							}}
						/>
					</Form.Item>

					{rawInput.trim() && errors.length < 1 && (
						<div className="flex justify-center">
							<Button type="primary" onClick={handleParseButton}>
								Add Transfer Scenarios
							</Button>
						</div>
					)}
				</div>
			)}

			{inputMethod === 'file' && (
				<div className="mb-6">
					<div className="flex justify-center items-center gap-4">
						{!uploadedFile && (
							<Tooltip
								title={
									<>
										Download a sample file to see the format.
										<br />• Set the column to <b>Text</b> (Right-click → Format Cells → Text)
										<br />• Enter numbers like <b>+1234567890</b> to keep formatting intact
									</>
								}
							>
								<Button size="middle" type="primary" onClick={handleDownloadTemplate}>
									Download Sample File
								</Button>
							</Tooltip>
						)}

						{!uploadedFile ? (
							<Tooltip title="Upload a .xlsx, .xls, or .csv file with 'scenario' and 'number' columns. Only one file allowed.">
								<Upload
									accept=".xlsx,.xls,.csv"
									maxCount={1}
									showUploadList={false}
									beforeUpload={(file) => handleFileUpload(file)}
								>
									<Button size="middle" type="default" icon={<UploadOutlined />}>
										Upload File
									</Button>
								</Upload>
							</Tooltip>
						) : (
							<div className="flex items-center gap-3 px-4 py-2 bg-green-50 rounded-lg">
								<span className="text-sm text-green-700">✔️ {uploadedFile.name}</span>
								<Button size="small" danger onClick={handleClearFile}>
									<DeleteFilled />
								</Button>
							</div>
						)}
					</div>
				</div>
			)}

			{errors.length > 0 && (
				<div className="mb-6">
					<Alert
						type="warning"
						message={
							<ul className="m-0 pl-5">
								{errors.map((err, i) => (
									<li key={i}>{err}</li>
								))}
							</ul>
						}
					/>
				</div>
			)}

			{value.length > 0 && (
				<div className="space-y-4">
					{showTable && (
						<Suspense
							fallback={
								<div className="text-center py-8">
									<Spin size="large" />
								</div>
							}
						>
							<LazyTable
								size="small"
								columns={columns}
								dataSource={value.map((v, i) => ({ ...v, key: i }))}
								pagination={false}
							/>
						</Suspense>
					)}

					<Popconfirm
						title="Are you sure you want to delete all scenarios?"
						onConfirm={handleDeleteAll}
						okText="Yes"
						cancelText="No"
					>
						<Button danger className="w-full">
							Delete All {value.length} Entries
						</Button>
					</Popconfirm>
				</div>
			)}
		</div>
	);
};

export default BulkTransfersFormSection;
